import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  getFieldPosition,
  scrollToInvalidField,
} from 'src/components/organisms/Personalisation/utils';
import { addItemToBasket, updateItemInBasket } from 'src/redux/basket/thunks';
import {
  selectInvalidCustomPersonalisations,
  selectIsPersonalisationInEditMode,
  selectOutstandingPersonalisations,
  selectTrackedEvents,
} from 'src/redux/personalisation/selectors';
import type { TrackedEvents } from 'src/redux/personalisation/types';
import { selectPersonalisationOptions, selectProduct } from 'src/redux/product/selectors';
import type { AppThunkDispatch } from 'src/redux/store';
import type { ReduxApplicationState } from 'src/redux/types';
import type { CatServiceAPIProductOption } from 'src/services/cat-service-api/types/CatServiceAPIProductOption';

const trackOutstandingSelections = (
  outstandingSelections: CatServiceAPIProductOption[],
  productOptions: CatServiceAPIProductOption[],
  trackedEvents: TrackedEvents,
  dispatch: AppThunkDispatch,
) => {
  outstandingSelections.forEach((opt) => {
    if (!trackedEvents.error['Add to Cart Error'][opt.name]) {
      const isMultipleOptionSelection = (opt.values ?? []).length > 3;
      const selectionLabel = isMultipleOptionSelection
        ? '3 and more values selection'
        : 'single & binary selection';

      const fieldType =
        opt.type === 'image'
          ? 'image upload'
          : opt.type === 'selection'
          ? selectionLabel
          : 'input field';

      dispatch({
        type: 'personalisation/trackInteractionError',
        payload: {
          field_action: 'Add to Cart Error',
          field_name: opt.name,
          field_position: getFieldPosition(productOptions, opt),
          field_type: fieldType,
          required_field: opt.required,
        },
      });
    }
  });
};

interface ThunkConfig {
  dispatch: AppThunkDispatch;
  state: ReduxApplicationState;
}

export const submitPersonalisationForm = createAsyncThunk<void, void, ThunkConfig>(
  'personalisation/submitPersonalisationForm',
  async (_, { dispatch, getState }) => {
    const state = getState();
    const outstandingSelections = selectOutstandingPersonalisations(state);
    const invalidCustomSelections = selectInvalidCustomPersonalisations(state);
    const isPersonalisationInEditMode = !!selectIsPersonalisationInEditMode(state);
    const product = selectProduct(state);
    const trackedEvents = selectTrackedEvents(state);
    const hasOptions = selectPersonalisationOptions(state).length > 0;
    const isDrawerOpen = state.personalisation.isPersonalisationOptionsExpanded;

    // When the product has options, the page CTA opens the options drawer rather
    // than submitting. The in-drawer CTA (drawer already open) falls through to
    // validate and add to basket.
    if (hasOptions && !isDrawerOpen) {
      dispatch({ type: 'personalisation/setPersonalisationExpanded', payload: { expanded: true } });
      return;
    }

    dispatch({ type: 'personalisation/showPersonalisationValidationErrors' });

    if (
      outstandingSelections.length > 0 ||
      invalidCustomSelections.length > 0 ||
      !state.personalisation.isQuantityValid
    ) {
      dispatch({ type: 'personalisation/setPersonalisationExpanded', payload: { expanded: true } });

      trackOutstandingSelections(outstandingSelections, product.options, trackedEvents, dispatch);

      scrollToInvalidField();
      return;
    }

    const result = isPersonalisationInEditMode
      ? await dispatch(updateItemInBasket())
      : await dispatch(addItemToBasket());

    // Close the drawer only once the item has been successfully added/updated,
    // so a failed request leaves the drawer open for the user to retry.
    if (addItemToBasket.fulfilled.match(result) || updateItemInBasket.fulfilled.match(result)) {
      dispatch({
        type: 'personalisation/setPersonalisationExpanded',
        payload: { expanded: false },
      });
    }
  },
);
